/**
 * @fileoverview Analytics Service - Event tracking and session management
 * 
 * @description
 * Provides comprehensive analytics tracking for user behavior, business
 * events, and AI interactions. Implements session-based tracking with
 * automatic deduplication and batching.
 * 
 * ## Session Management
 * ```
 * User Visit → getOrCreateSession() → 30-min timeout
 *                   ↓
 *           analytics_sessions table
 *                   ↓
 *           Track events with session_id
 *                   ↓
 *           endSession() on tab close
 * ```
 * 
 * ## Event Categories
 * | Category    | Usage                           |
 * |-------------|----------------------------------|
 * | page_view   | Route navigation tracking        |
 * | user_action | Button clicks, form submissions  |
 * | system      | Errors, performance metrics      |
 * | ai          | AI interactions, suggestions     |
 * | business    | Conversions, purchases           |
 * 
 * ## Performance Optimizations
 * - **Dev mode skip**: No tracking in development
 * - **Batch queuing**: Events batched (10 events or 5s)
 * - **Session caching**: Session ID cached in sessionStorage
 * 
 * @example
 * ```ts
 * import { analytics } from '@/lib/analytics';
 * 
 * // Track page view
 * analytics.trackPageView('/dashboard');
 * 
 * // Track user action
 * analytics.trackAction('button_click', { button: 'submit_form' });
 * 
 * // Track conversion
 * analytics.trackConversion('subscription', { plan: 'pro' });
 * 
 * // Track AI event
 * analytics.trackAI('suggestion_accepted', { type: 'schedule' });
 * ```
 * 
 * @module lib/analytics
 * @see analytics_sessions - Session storage table
 * @see analytics_events - Event storage table
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Event category types for classification
 * @typedef {'page_view' | 'user_action' | 'system' | 'ai' | 'business'} EventCategory
 */
export type EventCategory = 'page_view' | 'user_action' | 'system' | 'ai' | 'business';

/**
 * Analytics event structure
 * 
 * @interface AnalyticsEvent
 * @property {EventCategory} category - Event classification
 * @property {string} name - Specific event name (e.g., 'button_clicked')
 * @property {Record<string, any>} [data] - Custom event data
 * @property {string} [pagePath] - Override current page path
 * @property {string} [referrer] - Override document referrer
 */
export interface AnalyticsEvent {
  category: EventCategory;
  name: string;
  data?: Record<string, any>;
  pagePath?: string;
  referrer?: string;
}

// Session management
class AnalyticsSession {
  private static SESSION_KEY = 'actrus_analytics_session';
  private static SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  private static eventQueue: AnalyticsEvent[] = [];
  private static batchSize = 10;
  private static batchTimeout = 5000; // 5 seconds
  private static batchTimer: NodeJS.Timeout | null = null;
  private static isProcessing = false;

  static async getOrCreateSession(): Promise<string> {
    const stored = sessionStorage.getItem(this.SESSION_KEY);
    
    if (stored) {
      const { sessionId, timestamp } = JSON.parse(stored);
      const now = Date.now();
      
      // Check if session is still valid
      if (now - timestamp < this.SESSION_TIMEOUT) {
        // Update timestamp
        sessionStorage.setItem(
          this.SESSION_KEY,
          JSON.stringify({ sessionId, timestamp: now })
        );
        return sessionId;
      }
    }

    // Create new session
    return this.createNewSession();
  }

  private static async createNewSession(): Promise<string> {
    const sessionId = crypto.randomUUID();
    const timestamp = Date.now();
    
    const { data: { user } } = await supabase.auth.getUser();
    
    // Get device info
    const deviceInfo = this.getDeviceInfo();
    
    // Get referrer info
    const { source, medium, campaign } = this.parseReferrer();
    
    // Create session in database - silently handle RLS/auth failures
    // (e.g., unauthenticated crawlers like Lighthouse)
    const { error } = await supabase.from('analytics_sessions').insert({
      id: sessionId,
      user_id: user?.id || null,
      entry_page: window.location.pathname,
      device_info: deviceInfo,
      referrer_source: source,
      referrer_medium: medium,
      referrer_campaign: campaign,
      started_at: new Date().toISOString(),
      last_activity_at: new Date().toISOString(),
    });

    // Silently ignore auth/RLS errors - analytics should not block or log errors
    if (error && error.code !== 'PGRST301' && error.message?.includes('401') === false) {
      // Only log non-auth errors in dev for debugging
      if (import.meta.env.DEV) {
        console.warn('Analytics session error:', error.message);
      }
    }

    // Store in session storage regardless of DB success
    sessionStorage.setItem(
      this.SESSION_KEY,
      JSON.stringify({ sessionId, timestamp })
    );

    return sessionId;
  }

  static getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  static parseReferrer() {
    const url = new URL(window.location.href);
    const utmSource = url.searchParams.get('utm_source');
    const utmMedium = url.searchParams.get('utm_medium');
    const utmCampaign = url.searchParams.get('utm_campaign');

    return {
      source: utmSource || document.referrer || 'direct',
      medium: utmMedium || 'organic',
      campaign: utmCampaign || null,
    };
  }

  static async trackEvent(event: AnalyticsEvent) {
    // Only track in production to save costs during development
    if (import.meta.env.DEV) {
      return;
    }
    try {
      const sessionId = await this.getOrCreateSession();
      const { data: { user } } = await supabase.auth.getUser();

      // Get company ID from user profile if available
      let companyId = null;
      if (user) {
        const { data: member } = await supabase
          .from('company_members')
          .select('company_id')
          .eq('user_id', user.id)
          .eq('verified', true)
          .limit(1)
          .maybeSingle();
        
        companyId = member?.company_id || null;
      }

      const eventData = {
        session_id: sessionId,
        user_id: user?.id || null,
        company_id: companyId,
        event_category: event.category,
        event_name: event.name,
        event_data: event.data || {},
        page_path: event.pagePath || window.location.pathname,
        referrer: event.referrer || document.referrer,
        device_info: this.getDeviceInfo(),
        location_data: {}, // Can be enhanced with geo-location later
        created_at: new Date().toISOString(),
      };

      // Add to queue
      this.eventQueue.push(event);

      // Insert event - silently handle RLS/auth failures for unauthenticated users
      const { error } = await supabase.from('analytics_events').insert(eventData);
      
      // Silently ignore auth/RLS errors - analytics should not pollute console
      if (error && error.code !== 'PGRST301' && !error.message?.includes('401')) {
        // Only log non-auth errors in dev for debugging
        if (import.meta.env.DEV) {
          console.warn('Analytics event error:', error.message);
        }
      }

      // Process queue if it reaches batch size
      if (this.eventQueue.length >= this.batchSize) {
        this.processQueue();
      } else if (!this.batchTimer) {
        // Set timer for batch processing
        this.batchTimer = setTimeout(() => this.processQueue(), this.batchTimeout);
      }
    } catch {
      // Silently ignore all analytics errors - should never block user experience
    }
  }

  private static processQueue() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    // Queue is processed automatically by individual inserts
    // This is a placeholder for future batch optimization
    this.eventQueue = [];
  }

  static async endSession() {
    const stored = sessionStorage.getItem(this.SESSION_KEY);
    if (!stored) return;

    try {
      const { sessionId } = JSON.parse(stored);
      
      // Silently update - ignore auth/RLS errors for unauthenticated users
      await supabase
        .from('analytics_sessions')
        .update({
          ended_at: new Date().toISOString(),
        })
        .eq('id', sessionId);
    } catch {
      // Silently ignore - analytics should never throw
    }

    sessionStorage.removeItem(this.SESSION_KEY);
  }
}

// Public API
export const analytics = {
  // Track a page view
  trackPageView: (pagePath?: string) => {
    AnalyticsSession.trackEvent({
      category: 'page_view',
      name: 'page_viewed',
      data: {},
      pagePath: pagePath || window.location.pathname,
    });
  },

  // Track a user action
  trackAction: (actionName: string, data?: Record<string, any>) => {
    AnalyticsSession.trackEvent({
      category: 'user_action',
      name: actionName,
      data,
    });
  },

  // Track a business event
  trackBusiness: (eventName: string, data?: Record<string, any>) => {
    AnalyticsSession.trackEvent({
      category: 'business',
      name: eventName,
      data,
    });
  },

  // Track an AI event
  trackAI: (eventName: string, data?: Record<string, any>) => {
    AnalyticsSession.trackEvent({
      category: 'ai',
      name: eventName,
      data,
    });
  },

  // Track a system event
  trackSystem: (eventName: string, data?: Record<string, any>) => {
    AnalyticsSession.trackEvent({
      category: 'system',
      name: eventName,
      data,
    });
  },

  // Track a conversion
  trackConversion: (conversionType: string, data?: Record<string, any>) => {
    const stored = sessionStorage.getItem('actrus_analytics_session');
    if (stored) {
      const { sessionId } = JSON.parse(stored);
      supabase
        .from('analytics_sessions')
        .update({
          converted: true,
          conversion_type: conversionType,
        })
        .eq('id', sessionId)
        .then(() => {
          AnalyticsSession.trackEvent({
            category: 'business',
            name: 'conversion',
            data: { conversion_type: conversionType, ...data },
          });
        });
    }
  },

  // End current session
  endSession: () => AnalyticsSession.endSession(),
};

// Auto-track page visibility changes
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      analytics.endSession();
    }
  });
}
